import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StreakEvent } from '../types';
import { useStreak } from '../contexts/StreakContext';
import StreakCelebrationModal from './StreakCelebrationModal';
import StreakErrorBoundary from './StreakErrorBoundary';
import { withStreakErrorHandling, parseErrorMessage } from '../utils/errorHandling';

const StreakNotificationManager: React.FC = () => {
  const { registerStreakEventHandler, markCelebrationShown } = useStreak();

  // State for managing notifications
  const [celebrationEvent, setCelebrationEvent] = useState<StreakEvent | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // Store functions in refs to avoid stale closures
  const markCelebrationShownRef = useRef(markCelebrationShown);
  const registerStreakEventHandlerRef = useRef(registerStreakEventHandler);

  // Update refs when functions change
  useEffect(() => {
    markCelebrationShownRef.current = markCelebrationShown;
    registerStreakEventHandlerRef.current = registerStreakEventHandler;
  });

  // Mount debug
  useEffect(() => {
    console.log('[Streak] NotificationManager mounted');
  }, []);

  // Set up the event handler when component mounts with error handling
  useEffect(() => {
    const handleStreakEvent = withStreakErrorHandling(
      (event: StreakEvent) => {
        console.log('🎉 Received streak event:', event);

        if (!event) {
          console.warn('Received null streak event');
          return;
        }

        if (event.type === 'missed') {
          console.log('💔 Ignored missed streak event');
          // No longer handling streak missed events per request
        } else {
          console.log('🎊 Handling celebration event:', event.type);
          // Handle celebration events (started, continued, milestone)
          setCelebrationEvent(event);
          setShowCelebration(true);
          console.log('🎭 Set celebration state:', { event, showCelebration: true });
        }
      },
      undefined,
      (error) => {
        console.error('Error handling streak event:', error);
        const errorInfo = parseErrorMessage(error);
        console.error('Streak event handling failed:', errorInfo.message);
      }
    );

    registerStreakEventHandlerRef.current(handleStreakEvent);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCelebrationDismiss = useCallback(() => {
    console.log('🎬 handleCelebrationDismiss called');
    setShowCelebration(false);
    setCelebrationEvent(null);
    markCelebrationShownRef.current();
    console.log('🎬 Celebration state cleared');
  }, []);

  // Fallback event so the component can always be mounted (needed for exit animation)
  const fallbackEvent: StreakEvent = {
    type: 'continued',
    previousStreak: 0,
    newStreak: 0,
    timestamp: new Date(),
  };

  return (
    <StreakErrorBoundary>
      {/* Streak Banner — always mounted so AnimatePresence can animate exit */}
      <StreakCelebrationModal
        isVisible={showCelebration && !!celebrationEvent}
        streakEvent={celebrationEvent ?? fallbackEvent}
        onDismiss={handleCelebrationDismiss}
      />
    </StreakErrorBoundary>
  );
};

export default StreakNotificationManager;
